import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { parseMoneyToMinorUnits, formatMoney, minorUnitsToDecimalString } from '../../lib/money';

/**
 * Domain A — Tenant360 → Commercial → Transactions & Refunds (SuperAdmin tenant-specific refund authority).
 * Backend-authoritative over /super-admin/commercial/tenant/{ref}/refunds*. NEVER invents state; every action
 * posts to the governed route and re-reads. Execute is the ONLY money-moving action and is confirmed through a
 * governed in-app modal (NOT window.prompt — a browser prompt was found to receive autofilled/injected input):
 * a controlled React text field whose value must deterministically parse to the approved integer cents before the
 * destructive Execute button enables. The backend re-validates the exact integer. No Stripe secret/id is shown.
 */
const BASE = '/api/v1/super-admin/commercial';
// currency-generic display — the amount's own currency drives symbol + minor-unit digits (never a hardcoded $/2dp).
const money = (c, currency) => (c == null ? '—' : formatMoney(c, currency || 'USD'));
const TONE = {
  REQUESTED: 'bg-gray-100 text-gray-700', UNDER_REVIEW: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-indigo-100 text-indigo-800', EXECUTING: 'bg-amber-100 text-amber-800',
  PROVIDER_PENDING: 'bg-amber-100 text-amber-800', PARTIALLY_REFUNDED: 'bg-teal-100 text-teal-800',
  REFUNDED: 'bg-green-100 text-green-800', RECONCILED: 'bg-green-200 text-green-900',
  REJECTED: 'bg-gray-200 text-gray-600', FAILED: 'bg-red-100 text-red-700', CANCELLED: 'bg-gray-200 text-gray-600',
};
const Pill = ({ v }) => <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TONE[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span>;
// governed next-actions per state (mirrors backend ALLOWED_TRANSITIONS; execute is money-moving → modal)
const ACTIONS = {
  REQUESTED: [['review', 'Review'], ['reject', 'Reject']],
  UNDER_REVIEW: [['approve', 'Approve'], ['reject', 'Reject']],
  APPROVED: [['execute', 'Execute refund']],
  PROVIDER_PENDING: [['reconcile', 'Reconcile']],
  PARTIALLY_REFUNDED: [['execute', 'Execute remaining'], ['reconcile', 'Reconcile']],
  FAILED: [['execute', 'Retry execute']],
  REFUNDED: [['reconcile', 'Reconcile']],
};
const isExecutable = (state) => (ACTIONS[state] || []).some(([v]) => v === 'execute');

// canExecuteRefund: UI affordance for the commercial.refund.execute capability. Backend remains authoritative
// (execute route is SuperAdmin-gated); when false we simply do not render the Execute affordance.
export default function RefundGovernancePanel({ api, tenantRef, canExecuteRefund = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);

  // ---- governed execute-confirmation modal state ----
  const [execOp, setExecOp] = useState(null);   // the operation being executed (null = closed)
  const [execInput, setExecInput] = useState('');
  const [execBusy, setExecBusy] = useState(false);
  const [execErr, setExecErr] = useState(null);
  const inFlight = useRef(false);               // hard double-submit guard (survives batched re-renders)

  const load = useCallback(async () => {
    if (!tenantRef) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`${BASE}/tenant/${tenantRef}/refunds`);
      setRows(r?.data?.refunds || []);
    } catch (e) { setErr(e?.response?.data?.detail?.code || e?.message || 'Could not load refunds.'); }
    finally { setLoading(false); }
  }, [api, tenantRef]);
  useEffect(() => { load(); }, [load]);

  const openExecute = (op) => { setExecErr(null); setExecInput(''); setExecBusy(false); inFlight.current = false; setExecOp(op); };
  const closeExecute = () => { if (execBusy || inFlight.current) return; setExecOp(null); setExecInput(''); setExecErr(null); };

  // non-money-moving governed actions (review/approve/reject/reconcile). Execute goes through the modal.
  const act = async (op, verb) => {
    if (busy) return;
    let body = {};
    if (verb === 'reject') {
      const reason = window.prompt('Reason for rejecting this refund request:'); if (reason == null) return; body = { reason };
    }
    setBusy(true); setToast(null);
    try {
      const res = await api.post(`${BASE}/tenant/${tenantRef}/refunds/${op.refund_operation_id}/${verb}`, body);
      const r = res?.data || {};
      setToast({ ok: r.ok !== false, msg: `Refund ${op.refund_operation_id.slice(0, 12)}… → ${r.state || r.reason || verb}` });
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setToast({ ok: false, msg: (d && (d.code || d.message)) || `Could not ${verb}.` });
    } finally { setBusy(false); }
  };

  // ---- execute readiness (all conditions must hold before the destructive button enables) ----
  const requestedCents = execOp ? Number(execOp.requested_cents) : null;
  const parsedCents = execOp ? parseMoneyToMinorUnits(execInput, execOp.currency) : null;  // deterministic, currency-generic
  const amountMatches = parsedCents != null && parsedCents === requestedCents;
  // provider readiness comes from the backend-derived boolean (provider_ready); raw provider/payment ids never reach us.
  const providerReady = !!(execOp && execOp.provider_ready);
  const executable = !!execOp && isExecutable(execOp.state) && Number(execOp.remaining_refundable_cents) > 0;
  const opReady = executable && providerReady;
  const canSubmitExecute = opReady && amountMatches && !execBusy;

  const submitExecute = async () => {
    if (inFlight.current || !canSubmitExecute) return;           // guard: exact match + ready + not in flight
    inFlight.current = true; setExecBusy(true); setExecErr(null);
    const op = execOp;
    try {
      const res = await api.post(`${BASE}/tenant/${tenantRef}/refunds/${op.refund_operation_id}/execute`,
        { confirm_amount_cents: op.requested_cents, reason: op.reason || 'refund execution' });
      const r = res?.data || {};
      setToast({ ok: r.ok !== false, msg: `Refund ${op.refund_operation_id.slice(0, 12)}… → ${r.state || r.reason || 'execute'}` });
      setExecOp(null); setExecInput('');
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // keep the modal open and show the truthful backend error (never a false success)
      setExecErr((d && (d.code || d.message)) || 'Could not execute refund.');
    } finally { inFlight.current = false; setExecBusy(false); }
  };

  // the plain decimal string the human must type, derived from the operation's own amount + currency (never hardcoded)
  const expectedDisplay = execOp ? minorUnitsToDecimalString(execOp.requested_cents, execOp.currency) : '';

  return (
    <Card className="bg-white shadow-md" data-testid="tenant360-transactions-refunds">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Transactions &amp; Refunds</CardTitle>
        <Button size="sm" variant="outline" disabled={busy || loading} onClick={load}>Refresh</Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {toast && <div className={`mb-2 p-2 rounded text-sm ${toast.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role="status">{toast.msg}</div>}
        {err && <div className="mb-2 p-2 rounded text-sm bg-red-50 text-red-700">{err}</div>}
        {loading ? <p className="text-sm text-gray-400">Loading refunds…</p>
          : rows.length === 0 ? <p className="text-sm text-gray-500">No refund operations for this tenant. A Business admin can request one from Billing → Purchase Receipts.</p>
          : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>{['Invoice', 'Product', 'Class', 'Captured', 'Refunded', 'Remaining', 'State', 'Reason', 'Requester', 'Approver', 'Actions'].map((h) => <th key={h} className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((op) => (
                  <tr key={op.refund_operation_id}>
                    <td className="px-2 py-1 font-mono text-xs">{op.invoice_number}</td>
                    <td className="px-2 py-1 text-xs">{op.product_key || '—'}</td>
                    <td className="px-2 py-1 text-xs">{op.product_class}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{money(op.captured_cents, op.currency)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{money(op.confirmed_refunded_cents, op.currency)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{money(op.remaining_refundable_cents, op.currency)}</td>
                    <td className="px-2 py-1"><Pill v={op.state} /></td>
                    <td className="px-2 py-1 text-xs max-w-[16rem] truncate" title={op.reason}>{op.reason || '—'}</td>
                    <td className="px-2 py-1 text-xs">{op.requested_by || '—'}</td>
                    <td className="px-2 py-1 text-xs">{op.approved_by || '—'}</td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1">
                        {(ACTIONS[op.state] || []).map(([verb, label]) => {
                          if (verb === 'execute') {
                            // §6 gate: capability AND provider_ready AND remaining refundable > 0 (backend authoritative)
                            if (!canExecuteRefund || !op.provider_ready || !(Number(op.remaining_refundable_cents) > 0)) return null;
                            return (
                              <button key={verb} disabled={busy} onClick={() => openExecute(op)}
                                className="px-2 py-0.5 text-xs rounded border disabled:opacity-50 border-red-300 text-red-700 hover:bg-red-50">
                                {label}
                              </button>
                            );
                          }
                          return (
                            <button key={verb} disabled={busy} onClick={() => act(op, verb)}
                              className="px-2 py-0.5 text-xs rounded border disabled:opacity-50 border-gray-300 text-gray-700 hover:bg-gray-50">
                              {label}
                            </button>
                          );
                        })}
                        {(!ACTIONS[op.state] || ACTIONS[op.state].length === 0) && <span className="text-xs text-gray-400">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        <p className="text-xs text-gray-400 mt-2">Servisfy commercial refunds only. Original receipts are immutable; a refund creates a linked record. Execute performs a server-side Stripe TEST refund. Stripe secrets are never shown.</p>
      </CardContent>

      {/* Governed execute-confirmation modal — replaces the browser prompt. Radix Dialog supplies focus-trap +
          Escape/overlay-cancel. The destructive button stays disabled until the controlled input parses to the
          exact approved integer cents; Enter does not submit (no form). */}
      <Dialog open={!!execOp} onOpenChange={(o) => { if (!o) closeExecute(); }}>
        <DialogContent className="max-w-md" data-testid="refund-execute-modal"
          onInteractOutside={(e) => { if (execBusy) e.preventDefault(); }}>
          {execOp && (
            <>
              <DialogHeader>
                <DialogTitle>Execute refund</DialogTitle>
                <DialogDescription>This sends a Stripe TEST refund to the original payment method. This action moves money.</DialogDescription>
              </DialogHeader>

              <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
                <dt className="col-span-1 text-gray-500">Tenant</dt><dd className="col-span-2 font-mono text-xs break-all">{execOp.tenant_id || tenantRef}</dd>
                <dt className="col-span-1 text-gray-500">Product</dt><dd className="col-span-2 font-medium">{execOp.product_key || execOp.product_class}</dd>
                <dt className="col-span-1 text-gray-500">Invoice</dt><dd className="col-span-2 font-mono text-xs break-all">{execOp.invoice_number}</dd>
                <dt className="col-span-1 text-gray-500">Currency</dt><dd className="col-span-2">{execOp.currency || 'USD'}</dd>
                <dt className="col-span-1 text-gray-500">Environment</dt><dd className="col-span-2"><span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Stripe TEST</span></dd>
                <dt className="col-span-1 text-gray-500">Captured</dt><dd className="col-span-2">{money(execOp.captured_cents, execOp.currency)}</dd>
                <dt className="col-span-1 text-gray-500">Refund amount</dt><dd className="col-span-2 font-semibold">{money(execOp.requested_cents, execOp.currency)}</dd>
                <dt className="col-span-1 text-gray-500">Remaining refundable</dt><dd className="col-span-2">{money(execOp.remaining_refundable_cents, execOp.currency)}</dd>
                <dt className="col-span-1 text-gray-500">Reason</dt><dd className="col-span-2 break-words">{execOp.reason || '—'}</dd>
              </dl>

              {!opReady && (
                <div className="p-2 rounded text-sm bg-red-50 text-red-700" role="alert">
                  {!executable
                    ? 'This operation is not in an executable state or has no remaining refundable amount.'
                    : 'No validated provider reference is available for this charge. Execution is blocked until the original payment reference can be resolved.'}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="refund-confirm-amount">Type {expectedDisplay} to confirm</Label>
                <Input id="refund-confirm-amount" name="refund-confirm-amount"
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} inputMode="decimal"
                  placeholder={expectedDisplay} value={execInput} disabled={execBusy || !opReady}
                  onChange={(e) => setExecInput(e.target.value)}
                  aria-invalid={execInput.length > 0 && !amountMatches} />
                {execInput.length > 0 && !amountMatches && (
                  <p className="text-xs text-red-600">Enter exactly {expectedDisplay} to confirm.</p>
                )}
              </div>

              {execErr && <div className="p-2 rounded text-sm bg-red-50 text-red-700" role="alert">{execErr}</div>}

              <DialogFooter>
                <Button variant="outline" onClick={closeExecute} disabled={execBusy}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={!canSubmitExecute} onClick={submitExecute} data-testid="refund-execute-confirm">
                  {execBusy ? 'Executing…' : 'Execute Refund'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
